import { setPushpinAttributesAction } from '../../redux/actions';
import T from 'i18n-react';
import lodashGet from 'lodash/get';
import lodashCloneDeep from 'lodash/cloneDeep';
import featureFlags, { FEATURE_FLAGS } from '../../utils/featureFlags';

export default class {

  constructor() {
    this.dispatch = null;
    this.defaultVisibility = true;
    this.creating = false;
    this.pushpinExtension = null;
    this.namespace = (typeof AutodeskNamespace !== 'undefined' ? AutodeskNamespace('Autodesk.BIM360.Extension.PushPin') : null); //TODO: ? eliminate reference to AutodeskNamespace global
    this.NEW_PUSHPIN_LABEL = T.translate('newPushpinLabel');
    this.handlePushpinModified = this.handlePushpinModified.bind(this);
    this.handlePushpinCreated = this.handlePushpinCreated.bind(this);
  }

  setPushpinExtension(pushpinExtension) {
    this.pushpinExtension = pushpinExtension;
    if (pushpinExtension) {
      this.namespace = AutodeskNamespace('Autodesk.BIM360.Extension.PushPin'); //TODO: ? eliminate reference to AutodeskNamespace global
    }
  }

  handlePushpinModified(event) {
    const { value } = event;

    // Mark pushpin as "new" so the pushpin middleware will know to clear it when the pushpin drop flow is ended
    value.set({ isNew: true });
    // Make sure to extract `data` out of the event's value only after manipulating `isNew`
    const { data } = value;

    // We use cloneDeep here because LMV mutates nested objects within the event's data
    const pushpinAttributes = { ...lodashCloneDeep(data) };
    this.dispatch(setPushpinAttributesAction(pushpinAttributes));
  }

  handlePushpinCreated(event) {
    const { value: { data } } = event;
    if (data.label === this.NEW_PUSHPIN_LABEL) {
      this.endCreateItem();
      if (featureFlags.get(FEATURE_FLAGS.RFIS_NEW_VIEWER_EVENTS)) {
        // Set handler for pushpin modified event (same event name is triggered so we cancel the "create" handler and set a "modified" one)
        this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_PREPARING_THUMBNAIL, this.handlePushpinCreated);
        this.pushpinExtension.addEventListener(this.namespace.PUSH_PIN_PREPARING_THUMBNAIL, this.handlePushpinModified);
      } else {
        this.pushpinExtension.addEventListener(this.namespace.PUSH_PIN_MODIFY_EVENT, this.handlePushpinModified);
      }
      this.setPushpinDraggable(data.id);
      this.handlePushpinModified(event);
    }
  }

  setDispatch(dispatchFunction) {
    this.dispatch = dispatchFunction;
  }

  createPushpin(issue) {
    if (issue.pushpinAttributes) {
      // When creating a pushpin, the current selection is automatically deselected by the viewer extension's internal logic.
      // We save the currently selected pushpin so we can select it after the creation of the new pushpin.
      const selectedItemId = lodashGet(this.pushpinExtension.pushPinManager.getSelectedItem(), 'data.id');

      const { location, objectId, viewerState, externalId, attributesVersion } = issue.pushpinAttributes;
      const pushpinStatus = issue.type && issue.status.indexOf(issue.type) === -1 ? `${issue.type}-${issue.status}` : issue.status; // add type only if missing from status
      this.pushpinExtension.loadItemsV2([{
        id: issue.id,
        label: issue.customIdentifier || T.translate('unspecifiedValue'),
        status: pushpinStatus,
        position: { x: location.x, y: location.y, z: location.z },
        type: issue.type,
        objectId,
        viewerState,
        externalId,
        attributesVersion,
      }]);
      this.pushpinExtension.setVisibleById(issue.id, this.defaultVisibility);
      selectedItemId && this.pushpinExtension.selectOne(selectedItemId);
    }
  }

  setDefaultVisibility(visibility) {
    this.defaultVisibility = visibility;
    const items = this.pushpinExtension.pushPinManager.items();
    items.map((item) => {
      this.pushpinExtension.setVisibleById(item.data.id, this.defaultVisibility);
    });
  }

  updatePushpin(pushpin) {
    const { id, label, status, position, type, objectId, viewerState, externalId, attributesVersion } = pushpin;
    const pushpinStatus = type && status.indexOf(type) === -1 ? `${type}-${status}` : status; // add type only if missing from status
    this.pushpinExtension.updateItemById(
      id,
      {
        id,
        label,
        status: pushpinStatus,
        position: { ...position },
        type,
        objectId,
        viewerState,
        externalId,
        attributesVersion,
      }
    );
    this.pushpinExtension.showAll();
  }

  selectPushpin(pushpinId) {
    const selectedItem = this.pushpinExtension.pushPinManager.getSelectedItem();
    if (selectedItem && selectedItem.data && selectedItem.data.id === pushpinId) {
      // No need to reselect an already selected pushpin
      return;
    }

    // when all pushpins are rendered the viewer deselects all of them one by one.
    // we need to make sure that if we select a pushpin, we do that only after all pushpins are rendered
    // to avoid the pushpin deselection. we do that by calling setTimeout as a hack until the viewer team will
    // find a solution. this hack is mentioned in BTLV-12926
    setTimeout(() => {
      const pushpinAttributes = this.pushpinExtension.getItemById(pushpinId);
      this.pushpinExtension.selectOne(pushpinId);
      this.dispatch(setPushpinAttributesAction(pushpinAttributes));
    }, 0);
  }

  setPushpinDraggable(pushpinId) {
    this.setDefaultVisibility(false);
    // if it's draggable, it should be visible:
    this.pushpinExtension.setVisibleById(pushpinId, true);
    this.pushpinExtension.setDraggableById(pushpinId, true);
  }

  deselectPushpins() {
    this.pushpinExtension && this.pushpinExtension.selectNone();
    this.dispatch(setPushpinAttributesAction(null));
  }

  clearSinglePushpin(pushpinId) {
    this.pushpinExtension.removeItemById(pushpinId);
  }

  startDropPushpin(issueStatus) {
    this.setDefaultVisibility(false);

    const pushpinStatus = `rfis-${issueStatus}`;

    // TODO - Why does the LMV startCreateItem method need issue-type encoded in status:pushpinStatus?
    // (startCreateItem already receives type:issueType as its own parameter)
    this.pushpinExtension.startCreateItem({ label: this.NEW_PUSHPIN_LABEL, status: pushpinStatus, type: 'rfis' });
    this.creating = true;
    const pushpinDroppedEventName = featureFlags.get(FEATURE_FLAGS.RFIS_NEW_VIEWER_EVENTS)
      ? this.namespace.PUSH_PIN_PREPARING_THUMBNAIL
      : this.namespace.PUSH_PIN_CREATED_EVENT;
    this.pushpinExtension.addEventListener(pushpinDroppedEventName, this.handlePushpinCreated);
  }

  endCreateItem() {
    if (this.creating) {
      // LMV .endCreateItem() sets visibility of all pushpins to true:
      this.pushpinExtension.endCreateItem();
      this.creating = false;
    }
  }

  endDropPushpin() {
    this.setDefaultVisibility(true);

    if (featureFlags.get(FEATURE_FLAGS.RFIS_NEW_VIEWER_EVENTS)) {
      // We don't know which handler is currently set for PUSH_PIN_PREPARING_THUMBNAIL event so we remove both
      this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_PREPARING_THUMBNAIL, this.handlePushpinCreated);
      this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_PREPARING_THUMBNAIL, this.handlePushpinModified);
    } else {
      this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_CREATED_EVENT, this.handlePushpinCreated);
      this.pushpinExtension.removeEventListener(this.namespace.PUSH_PIN_MODIFY_EVENT, this.handlePushpinModified);
    }

    this.dispatch(setPushpinAttributesAction(null));
    this.endCreateItem();
  }
}
